// tableFilter.tsx
import './tableFilter.css';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TextInput from '../../common/textInput/textInput';
import Checkbox from '../../common/checkbox/checkbox';
import { TableColumnConfig, JsonColumnConfig } from '../tableMain';
import { ArrowUpDown, ArrowUpAZ, ArrowDownAZ } from 'lucide-react';
import Dropdown from '../../common/dropdown/dropdown';

export interface ComparisonFilter {
    operator: '>' | '<' | '>=' | '<=' | '=' | 'between' | 'none';
    value1: string | number;
    value2?: string | number; // For 'between' operator
}

interface TableFilterProps {
    columnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    isOpen: boolean;
    position: { top: number; left: number };
    onClose: () => void;
    data: any[]; // Full filtered data for display
    originalData: any[]; // Full original unfiltered data
    columnName: string;
    onFilterApply: (columnName: string, checkedValues: Set<string>, isActive: boolean) => void;
    initialCheckedValues: Set<string>;
    onSortApply: (columnName: string, sortOrder: 'asc' | 'desc' | 'none') => void;
    currentSortOrder?: 'asc' | 'desc' | 'none';
    userData?: any;
    tableName: string;
}

const TableFilter: React.FC<TableFilterProps> = ({
    columnConfigs,
    jsonColumnConfig,
    isOpen,
    position,
    onClose,
    data,
    originalData,
    columnName,
    onFilterApply,
    initialCheckedValues,
    onSortApply,
    currentSortOrder = 'none',
    userData,
    tableName,
}) => {

    const [textFilterValue, setTextFilterValue] = useState('');
    const [checkedValues, setCheckedValues] = useState<Set<string>>(new Set());
    const [selectAllCheckbox, setSelectAllCheckbox] = useState<boolean>(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | 'none'>(currentSortOrder);
    const inputRef = useRef<HTMLInputElement>(null);
    const [comparisonOperator, setComparisonOperator] = useState<string>('none');
    const [comparisonValue1, setComparisonValue1] = useState<string>('');
    const [comparisonValue2, setComparisonValue2] = useState<string>('');

    // Get the current column configuration
    const getCurrentColumnConfig = (): TableColumnConfig | undefined => {
        return columnConfigs.find(config => config.colName === columnName);
    };

    const currentColumnConfig = getCurrentColumnConfig();
    const isNumberColumn = currentColumnConfig?.colType === 'number' || currentColumnConfig?.colType === 'accounting' || currentColumnConfig?.colType === 'calculated';

    // Get unique values from the column data, including empty values
    const getUniqueValues = (): string[] => {
        const values = new Set<string>();
        let hasEmpty = false;
        
        // Find the column config for the current column
        const columnConfig = columnConfigs.find(config => config.colName === columnName);
        const isJsonColumn = columnConfig?.colType === 'json';
        
        data.forEach(row => {
            let value = row[columnName];
            
            // Apply conditionalDisplay if defined
            if (columnConfig?.conditionalDisplay && userData) {
                value = columnConfig.conditionalDisplay(value, row, userData);
            }
            
            if (value === null || value === undefined || value === '') {
                hasEmpty = true;
            } else {
                let displayValue: string;
            
                if (isJsonColumn) {
                    try {
                        if (jsonColumnConfig && jsonColumnConfig[0].SQLTable) {
                            console.log('JSON column with SQLTable:', jsonColumnConfig[0].SQLTable);
                            displayValue = '';
                        } else {

                            // Handle JSON column - convert to "X items" format
                            const jsonData = typeof value === 'string' ? JSON.parse(value) : value;
                            if (Array.isArray(jsonData)) {
                                displayValue = `${jsonData.length} items`;
                            } else if (typeof jsonData === 'object') {
                                const keys = Object.keys(jsonData);
                                displayValue = `${keys.length} items`;
                            } else {
                                displayValue = String(value);
                            }
                        }
                    } catch (error) {
                        // If JSON parsing fails, treat as regular string
                        displayValue = String(value);
                    }
                } else {
                    // Handle regular column
                    displayValue = String(value);
                }
                
                values.add(displayValue);
            }
        });
        
        const sortedValues = isNumberColumn 
            ? Array.from(values).sort((a, b) => parseFloat(a) - parseFloat(b))
            : Array.from(values).sort();
        
        // Add "(Empty)" at the beginning if there are empty values
        if (hasEmpty) {
            return ['(Empty)', ...sortedValues];
        }
        
        return sortedValues;
    };
    
    const uniqueValues = getUniqueValues();

    // Filter the displayed options based on search input
    const getFilteredOptions = (): string[] => {
        if (!textFilterValue.trim()) {
            return uniqueValues; // Show all available options when no search
        }
        
        return uniqueValues.filter(value => 
            value.toLowerCase().includes(textFilterValue.toLowerCase())
        );
    };

    const filteredOptions = getFilteredOptions();
    
    // Get all possible values from original data
    const getAllPossibleValues = (): string[] => {
        const values = new Set<string>();
        let hasEmpty = false;
        
        // Find the column config for the current column
        const columnConfig = columnConfigs.find(config => config.colName === columnName);
        const isJsonColumn = columnConfig?.colType === 'json';
        
        originalData.forEach(row => {
            let value = row[columnName];
            
            // Apply conditionalDisplay if defined
            if (columnConfig?.conditionalDisplay && userData) {
                value = columnConfig.conditionalDisplay(value, row, userData);
            }
            
            if (value === null || value === undefined || value === '') {
                hasEmpty = true;
            } else {
                let displayValue: string;
                
                if (isJsonColumn) {
                    // Handle JSON column - convert to "X items" format
                    try {
                        const jsonData = typeof value === 'string' ? JSON.parse(value) : value;
                        if (Array.isArray(jsonData)) {
                            displayValue = `${jsonData.length} items`;
                        } else if (typeof jsonData === 'object') {
                            const keys = Object.keys(jsonData);
                            displayValue = `${keys.length} items`;
                        } else {
                            displayValue = String(value);
                        }
                    } catch (error) {
                        // If JSON parsing fails, treat as regular string
                        displayValue = String(value);
                    }
                } else {
                    // Handle regular column
                    displayValue = String(value);
                }
                
                values.add(displayValue);
            }
        });
        
        const sortedValues = Array.from(values).sort();
        
        if (hasEmpty) {
            return ['(Empty)', ...sortedValues];
        }
        
        return sortedValues;
    };

    const allPossibleValues = getAllPossibleValues();
    
    // Helper function to determine if filter is active
    const isFilterActive = (checkedSet: Set<string>): boolean => {
        // Compare against ALL possible values, not just currently available ones
        const allPossibleValuesChecked = checkedSet.size === allPossibleValues.length && 
                                        allPossibleValues.every(val => checkedSet.has(val));
        
        // Filter is active when not all possible values are checked
        return !allPossibleValuesChecked;
    };

    // Reset checked values when column data changes (due to other filters)
    useEffect(() => {
        // Only initialize when the filter is open
        if (!isOpen) return;

        // Reset the search input when reopening
        setTextFilterValue('');

        if (initialCheckedValues.size > 0) {
            // Intersect initial values with currently available values
            const availableValues = new Set(uniqueValues);
            const validCheckedValues = new Set(
                Array.from(initialCheckedValues).filter(value => availableValues.has(value))
            );
            setCheckedValues(validCheckedValues);
        } else {
            // Default to all available values checked
            const allValues = new Set(uniqueValues);
            setCheckedValues(allValues);
        }
        setIsInitialized(true);
    }, [isOpen, columnName, JSON.stringify(uniqueValues)]); // Depend on uniqueValues changes

    // Apply initial filter only once after initialization
    useEffect(() => {
        if (isInitialized && initialCheckedValues.size === 0) {
            const allValues = new Set(uniqueValues.map(value => String(value)));
            // Mark as inactive since all values are checked and no search
            onFilterApply(columnName, allValues, false);
        }
    }, [isInitialized]); // Only run once after initialization

    // Reset initialization flag when filter closes
    useEffect(() => {
        if (!isOpen) {
            setIsInitialized(false);
        }
    }, [isOpen]);

    // Focus the input when the component opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleCheckboxClick = (value: any) => {
        const stringValue = String(value);
        const newCheckedValues = new Set(checkedValues);
        
        if (newCheckedValues.has(stringValue)) {
            // Prevent unchecking if this is the last checked value
            if (newCheckedValues.size === 1) {
                return; // Do nothing - can't uncheck the last value
            }
            newCheckedValues.delete(stringValue);
        } else {
            newCheckedValues.add(stringValue);
        }
        
        setCheckedValues(newCheckedValues);
        
        // Apply filter immediately with active state (no search value)
        const isActive = isFilterActive(newCheckedValues);
        onFilterApply(columnName, newCheckedValues, isActive);
    };
    
    // Handle search input changes
    const handleSearchChange = (value: string) => {
        setTextFilterValue(value);
        
        if (value.trim() && tableName !== 'bottoms_schedule_table') {
            // When searching, only keep values that match the search
            const matchingValues = new Set(
                uniqueValues.filter(uniqueValue => 
                    uniqueValue.toLowerCase().includes(value.toLowerCase())
                )
            );
            
            // Update checked values to only include matching ones
            const newCheckedValues = new Set<string>();
            checkedValues.forEach(checkedValue => {
                if (matchingValues.has(checkedValue)) {
                    newCheckedValues.add(checkedValue);
                }
            });

            // Ensure at least one value is checked if any matches exist
            if (newCheckedValues.size === 0 && matchingValues.size > 0) {
                const firstMatch = Array.from(matchingValues)[0];
                newCheckedValues.add(firstMatch);
            }
            
            setCheckedValues(newCheckedValues);
            
            // Apply filter immediately
            const isActive = isFilterActive(newCheckedValues);
            onFilterApply(columnName, newCheckedValues, isActive);
        }
        // When clearing search, keep current checked state as is
    };

    // Calculate if all values are checked
    const isAllChecked = checkedValues.size === uniqueValues.length && 
                        uniqueValues.every(value => checkedValues.has(value));

    // Handle select all checkbox click
    const handleSelectAllCheckbox = (currentSelectAllState: boolean) => {
        if (currentSelectAllState) {
            // Currently all are selected, so uncheck all (visual only - no actual filtering)
            const newCheckedValues = new Set<string>();
            setCheckedValues(newCheckedValues);
            
            // Pass inactive filter (show all data)
            onFilterApply(columnName, newCheckedValues, false);
        } else {
            // Currently not all are selected, so select all
            const allValues = new Set(uniqueValues);
            setCheckedValues(allValues);
            
            const isActive = isFilterActive(allValues);
            onFilterApply(columnName, allValues, isActive);
        }
    };

    // Update selectAllCheckbox state whenever checkedValues changes
    useEffect(() => {
        setSelectAllCheckbox(isAllChecked);
    }, [checkedValues, uniqueValues]);

    // sort logic
    // Handle sort button click
    const handleSortClick = () => {
        let newSortOrder: 'asc' | 'desc' | 'none';
        
        if (sortOrder === 'none') {
            newSortOrder = 'asc';
        } else if (sortOrder === 'asc') {
            newSortOrder = 'desc';
        } else {
            newSortOrder = 'none';
        }
        
        setSortOrder(newSortOrder);
        onSortApply(columnName, newSortOrder);
    };

    // Get sort icon based on current sort order
    const getSortIcon = () => {
        switch (sortOrder) {
            case 'asc':
                return <ArrowUpAZ size={16} />;
            case 'desc':
                return <ArrowDownAZ size={16} />;
            default:
                return <ArrowUpDown size={16} />;
        }
    };

    // Get sort label based on current sort order
    const getSortLabel = () => {
        switch (sortOrder) {
            case 'asc':
                return 'Ascending';
            case 'desc':
                return 'Descending';
            default:
                return 'Default';
        }
    };

    // Update sort order when currentSortOrder prop changes
    useEffect(() => {
        setSortOrder(currentSortOrder);
    }, [currentSortOrder]);

    // Define comparison options
    const comparisonOptions = [
        { value: 'none', label: 'No filter' },
        { value: '>', label: 'Greater than' },
        { value: '<', label: 'Less than' },
        { value: '>=', label: 'Greater than or equal to' },
        { value: '<=', label: 'Less than or equal to' },
        { value: '=', label: 'Equal to' },
        { value: 'between', label: 'Between values' },
    ];

    // Reset comparison values when operator changes or filter closes
    useEffect(() => {
        if (comparisonOperator === 'none' || initialCheckedValues.size === 0) {
            setComparisonOperator('none');
            setComparisonValue1('');
            setComparisonValue2('');
        }
    }, [isOpen]);

    // Handle comparison operator change
    const handleComparisonChange = (selectedValue: string) => {
        setComparisonOperator(selectedValue);
        
        // Apply comparison filter immediately if values exist
        if (selectedValue !== 'none') {
            applyComparisonFilter(selectedValue, comparisonValue1, comparisonValue2);
        } else {
            // Reset to show all data when no comparison filter
            const allValues = new Set(uniqueValues);
            setCheckedValues(allValues);
            onFilterApply(columnName, allValues, false);
        }
    };

    // Handle comparison value changes
    const handleComparisonValue1Change = (value: string) => {
        setComparisonValue1(value);
        applyComparisonFilter(comparisonOperator, value, comparisonValue2);
    };

    const handleComparisonValue2Change = (value: string) => {
        setComparisonValue2(value);
        applyComparisonFilter(comparisonOperator, comparisonValue1, value);
    };

    // Apply comparison filter logic
    const applyComparisonFilter = (operator: string, value1: string, value2: string) => {
        if (operator === 'none' || !value1.trim()) {
            // No filter or empty value - show all
            const allValues = new Set(uniqueValues);
            setCheckedValues(allValues);
            onFilterApply(columnName, allValues, false);
            return;
        }

        const numValue1 = parseFloat(value1);
        const numValue2 = parseFloat(value2);

        // Validate numeric inputs
        if (isNaN(numValue1) || (operator === 'between' && isNaN(numValue2))) {
            return; // Don't apply filter with invalid numbers
        }

        // Filter unique values based on comparison
        const filteredValues = new Set<string>();
        
        uniqueValues.forEach(uniqueValue => {
            if (uniqueValue === '(Empty)') {
                // Handle empty values separately - they don't pass numeric comparisons
                return;
            }

            const numericValue = parseFloat(uniqueValue);
            if (isNaN(numericValue)) {
                return; // Skip non-numeric values
            }

            let passes = false;
            
            switch (operator) {
                case '>':
                    passes = numericValue > numValue1;
                    break;
                case '<':
                    passes = numericValue < numValue1;
                    break;
                case '>=':
                    passes = numericValue >= numValue1;
                    break;
                case '<=':
                    passes = numericValue <= numValue1;
                    break;
                case '=':
                    passes = numericValue === numValue1;
                    break;
                case 'between':
                    const min = Math.min(numValue1, numValue2);
                    const max = Math.max(numValue1, numValue2);
                    passes = numericValue >= min && numericValue <= max;
                    break;
            }

            if (passes) {
                filteredValues.add(uniqueValue);
            }
        });

        // Update checked values and apply filter
        setCheckedValues(filteredValues);
        const isActive = filteredValues.size > 0 && filteredValues.size < uniqueValues.length;
        onFilterApply(columnName, filteredValues, isActive);
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="tableFilter-overlay"
            onClick={onClose}
        >
            <div 
                className="tableFilter-container"
                style={{ 
                    top: position.top, left: position.left,
                    height: isNumberColumn ? 375 : 325,
                }}
                onClick={(e) => e.stopPropagation()}
            >   
                <div className="tableFilter-searchContainer">
                    <TextInput
                        value={textFilterValue}
                        onChange={handleSearchChange}
                        placeholder={'Search'}
                        ref={inputRef}
                    />
                </div>

                <div className="tableFilter-sortContainer">
                    <div 
                        className="tableFilter-orderSort"
                        onClick={handleSortClick}
                    >
                        <button
                            className="tableFilter-sortButton"
                            style={{ color: sortOrder === 'none' ? '' : 'var(--color-blue-default)' }}
                        >
                            {getSortIcon()}
                            <span>{getSortLabel()}</span>
                        </button>
                    </div>
                    
                </div>

                {/* Only show comparison dropdown for number columns */}
                {isNumberColumn && (
                    <>
                        <div className="tableFilter-amountSort">
                            <Dropdown
                                options={comparisonOptions}
                                value={comparisonOperator}
                                onChange={handleComparisonChange}
                                className='dropdown-inputMatch'
                            />
                        </div>
                        {/* Show text input(s) when operator is not 'none' */}
                        {comparisonOperator !== 'none' && (
                            <div className="tableFilter-comparisonInputs">
                                {comparisonOperator === 'between' ? (
                                    <>
                                        <TextInput
                                            value={comparisonValue1}
                                            onChange={handleComparisonValue1Change}
                                            placeholder="Min value"
                                        />
                                        <TextInput
                                            value={comparisonValue2}
                                            onChange={handleComparisonValue2Change}
                                            placeholder="Max value"
                                        />
                                    </>
                                ) : (
                                    <TextInput
                                        value={comparisonValue1}
                                        onChange={handleComparisonValue1Change}
                                        placeholder={`Enter value`}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}

                <div 
                    className="tableFilter-valuesContainer"
                    style={{ maxHeight: isNumberColumn ? 200 : 225 }}
                >
                    <div className="tableFilter-selectAll">
                        <div 
                            className="tableFilter-valueItem"
                            onClick={() => handleSelectAllCheckbox(selectAllCheckbox)}
                        >   
                            <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    size={14}
                                    checked={selectAllCheckbox}
                                    onChange={() => handleSelectAllCheckbox(selectAllCheckbox)}
                                />
                            </div>
                            <div className="tableFilter-selectAllText">
                                (Select All)
                            </div>
                        </div>
                    </div>
                    {filteredOptions.map((value, index) => {
                        const stringValue = String(value);
                        const isChecked = checkedValues.has(stringValue);
                        const isLastChecked = isChecked && checkedValues.size === 1;

                        return (
                            <div
                                key={index}
                                className="tableFilter-valueItem"
                                onClick={() => handleCheckboxClick(value)}
                                style={{
                                    opacity: isLastChecked ? 0.6 : 1,
                                    cursor: isLastChecked ? 'default' : 'pointer',
                                }}
                            >
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        size={14}
                                        checked={isChecked}
                                        onChange={() => handleCheckboxClick(value)}
                                        isDisabled={isLastChecked}
                                    />
                                </div>
                                <div>
                                    {stringValue}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TableFilter;